import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

export default function Customers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });

  const load = () => api.get("/customers").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const payload = { ...form, email: form.email || null };
      await api.post("/customers", payload);
      toast.success("Customer added");
      setForm({ name: "", phone: "", email: "", address: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const pg = usePagination(list);
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Directory</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Customers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
              <Plus className="w-4 h-4 mr-1" /> New Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border">
            <DialogHeader><DialogTitle className="font-display uppercase">New Customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[
                ["name", "Full Name"], ["phone", "Phone"],
                ["email", "Email (optional)"], ["address", "Address"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{l}</Label>
                  <Input
                    data-testid={`cust-${k}`}
                    value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                    className="mt-1 rounded-sm bg-secondary border-border"
                  />
                </div>
              ))}
              <Button data-testid="save-customer-btn" onClick={submit} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Address</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (<tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No customers yet</td></tr>)}
            {pg.pageItems.map(c => (
              <tr key={c.id} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 font-mono text-xs">{c.phone}</td>
                <td className="p-3 text-muted-foreground">{c.email || "-"}</td>
                <td className="p-3 text-muted-foreground">{c.address || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
